import { RegisterRequest } from '@/types/register';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useRegisterRequestStore } from '@/store/registerRequestStore';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useState } from 'react';
import { RejectRequestDialog } from './RejectRequestDialog';

interface RegisterRequestDialogProps {
  request: RegisterRequest | null;
  selectedRole: 'user' | 'admin';
  onRoleChange: (role: 'user' | 'admin') => void;
  onClose: () => void;
}

export function RegisterRequestDialog({
  request,
  selectedRole,
  onRoleChange,
  onClose,
}: RegisterRequestDialogProps) {
  const { approveRequest, isLoading } = useRegisterRequestStore();
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  if (!request) return null;

  const handleApprove = async () => {
    try {
      await approveRequest(request.id, selectedRole);
      onClose();
    } catch (error) {
      console.error('Failed to approve request:', error);
    }
  };

  return (
    <>
      <Dialog open={!!request} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>تأكيد الطلب</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Request Info Summary */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium ml-1">مقدم الطلب:</span>
                {request.name}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium ml-1">رقم الجوال:</span>
                <span dir="ltr">{request.phoneNumber}</span>
              </p>
            </div>

            {/* Role Selection */}
            <div className="space-y-4">
              <Label>نوع الحساب</Label>
              <RadioGroup
                value={selectedRole}
                onValueChange={(value: 'user' | 'admin') => onRoleChange(value)}
                className="flex flex-col gap-2"
              >
                <div className="flex items-center justify-end gap-2">
                  <Label htmlFor="user" className="cursor-pointer text-right">
                    مستخدم عادي
                    <p className="text-xs text-gray-500 mt-1 ">
                      يمكنه عرض الخدمات الإلكترونية وتقديم الطلبات
                    </p>
                  </Label>
                  <RadioGroupItem value="user" id="user" />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Label htmlFor="admin" className="cursor-pointer text-right">
                    مسؤول
                    <p className="text-xs text-gray-500 mt-1">
                      يمكنه إدارة المستخدمين والطلبات والإعدادات
                    </p>
                  </Label>
                  <RadioGroupItem value="admin" id="admin" />
                </div>
              </RadioGroup>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              onClick={handleApprove}
              disabled={isLoading}
              className="hover:bg-secondary"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner className="ml-2" />
                  جاري القبول...
                </>
              ) : (
                'قبول'
              )}
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => setShowRejectDialog(true)}
              disabled={isLoading}
            >
              رفض
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RejectRequestDialog
        requestId={showRejectDialog ? request.id : null}
        requestName={request.name}
        onClose={() => {
          setShowRejectDialog(false);
          onClose();
        }}
      />
    </>
  );
}